/* eslint-disable no-unused-vars */
import qz, { PrintData } from 'qz-tray';
import React, { useEffect, useMemo, useState } from 'react';

import {
  LooseLeafBusinessInfo,
  GetTransactionsViaTransactionNoResponse,
  Transaction,
  TransactionPrintPageData,
  warranties,
  LooseLeafHistory,
  TransactionPayment,
  ClaimStubHistory
} from 'src/types';
import {
  anyToNumber,
  dateToday,
  formatCurrency,
  formatDate,
  getNumberOrZero,
  initializedWordsOtherThanFirst,
  isEmptyOrSpaces,
  promoPriceToSRP,
  secureText
} from 'src/utils';
import { RenderDetailEditor } from './components';
import { RenderRMAEditor } from './components/RenderRMAEditor';
import { clone, cloneDeep } from 'lodash';
import { ProductTypeEnum } from 'src/enums';
import { transactionDataCleaner } from './util';
import { PrintTransactionModal } from './components/PrintTransactionModal';
import { getMostCommonBranchIdOnTransactionItems } from 'src/redux/slices/branches/util';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import { PrintTransactionModalEU } from './components-eu/PrintTransactionModalEU';
import { PrintTransactionModalLooseLeaf } from './components-eu-looseleaf/PrintTransactionModalLooseLeaf';
import { Alert } from '@material-ui/lab';
import { SupabaseClientService } from 'src/services';
import useUserInfo from 'src/hooks/user/use-user-info';
import { ClaimStubPreviewModal } from './components/ClaimStubPreviewModal';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@material-ui/core';
import { useTransaction } from 'src/hooks/transaction/use-transaction';
import { slices, useAppSelector } from 'src/redux';
import { taptapGetCustomerDetailsService } from 'src/redux/slices/taptap-customer';

const linesLimitPerPageOnLooseLeaf = 54;

// Create Document Component
const MyDocument = () => {
  const sessionTransaction = JSON.parse(
    sessionStorage.getItem('transactionData') || ''
  );
  const { branchIDToName } = useBranchInfo();
  const { userDetails } = useUserInfo();

  const {
    onTransactionDetailsUpdate,
    getTransactionDetails,
    transactionDetailsResponse
  } = useTransaction();

  const [isSIPricing, setIsSIPricing] = useState<boolean>(false);
  const [isPrintOpen, setIsPrintOpen] = useState<boolean>(false);
  const [isLooseLeafPrintOpen, setIsLooseLeafPrintOpen] = useState<boolean>(
    false
  );
  const [isLooseLeaf2307PrintOpen, setIsLooseLeaf2307PrintOpen] = useState<
    boolean
  >(false);
  const [
    isLooseLeafVATExemptedPrintOpen,
    setIsLooseLeafVATExemptedPrintOpen
  ] = useState<boolean>(false);
  const [isPrintEUOpen, setIsPrintEUOpen] = useState<boolean>(false);
  const [isBlankPage, setIsBlankPage] = useState<boolean>(false);
  const [
    transactionStateData,
    setTransactionStateData
  ] = useState<GetTransactionsViaTransactionNoResponse | null>(null);
  const [
    prevTransactionState,
    setPrevTransactionState
  ] = useState<GetTransactionsViaTransactionNoResponse | null>();
  const [claimStubClickHistory, setClaimStubClickHistory] = useState<
    ClaimStubHistory[]
  >([]);
  const [
    looseLeafHistoryPreviewData,
    setLooseLeafHistoryPreviewData
  ] = useState<LooseLeafHistory | undefined>(undefined);
  const [claimStubHistoryPreview, setClaimStubHistoryPreview] = useState<
    ClaimStubHistory | undefined
  >(undefined);
  const [looseLeafBusinessInfo, setLooseLeafBusinessInfo] = useState<
    LooseLeafBusinessInfo
  >({});
  const [looseleafClickHistory, setLooseleafClickHistory] = useState<
    LooseLeafHistory[]
  >([]);

  //TODO: temporarily solution as long as accounting does not encountered any problem
  const [taptapCustomer, setTaptapCustomer] = useState<any>();

  const fetchTapTapCustomerDetails = async (id?: number) => {
    try {
      if (!id) {
        return;
      }

      const { data, error } = await taptapGetCustomerDetailsService(Number(id));

      if (data) {
        setTaptapCustomer(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const documentTitle = useMemo(() => {
    const firstName = transactionStateData?.customer?.first_name ?? '';
    const lastName = transactionStateData?.customer?.last_name ?? '';
    const transactionNo = transactionStateData?.transaction_no ?? '';
    return `${+new Date()}-${transactionNo}-${firstName}-${lastName}`;
  }, [transactionStateData]);

  const dateSold = useMemo(() => {
    if (
      transactionStateData?.items &&
      transactionStateData?.items?.length > 0
    ) {
      if (transactionStateData?.items[0]?.created_at) {
        return formatDate(transactionStateData?.items[0].created_at);
      }
      return '--';
    }
    return '--';
  }, [transactionStateData]);

  const onLooseleafClickHistoryFetch = async (transactionNoArg?: string) => {
    const { data, error } = await SupabaseClientService.from(
      'sales_invoice_logs'
    )
      .select('*')
      .eq(
        'internal_transaction_no',
        transactionNoArg || transactionStateData?.transaction_no
      )
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching looseleaf click history:', error);
      return;
    }
    if (data?.length > 0) {
      setLooseleafClickHistory(data);
    }
  };

  const onClaimStubClickHistoryFetch = async (transactionNoArg?: string) => {
    const { data, error } = await SupabaseClientService.from('claim_stub_logs')
      .select('*')
      .eq(
        'internal_transaction_no',
        transactionNoArg || transactionStateData?.transaction_no
      )
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching claim stub click history:', error);
      return;
    }
    if (data?.length > 0) {
      setClaimStubClickHistory(data);
    }
  };

  const onLooseLeafButtonClicked = async (
    isTwoThreeOSeven: boolean = false
  ) => {
    const looseLeafLogData: LooseLeafHistory = {
      internal_transaction_no: transactionStateData?.transaction_no,
      looseleaf_business_info: looseLeafBusinessInfo,
      looseleaf_page_data: looseLeafPageData,
      internal_created_by:
        userDetails?.first_name + ' ' + userDetails?.last_name,
      is_two_three_o_seven: isTwoThreeOSeven,
      date_sold: dateSold,
      released_by: releasedBy,
      store_branch: storeBranch,
      document_title: documentTitle,
      total_amount: totalPriceOnPrint,
      transaction_state_data: transactionStateData
    };

    const { error } = await SupabaseClientService.from(
      'sales_invoice_logs'
    ).insert({ ...looseLeafLogData });
    console.log(looseLeafLogData, error);
  };

  const onUpdateOfTransaction = async (
    newData: GetTransactionsViaTransactionNoResponse
  ) => {
    await onTransactionDetailsUpdate({
      ...newData,
      data: {
        id: newData?.id,
        ...(newData?.business_name !== transactionStateData?.business_name && {
          business_name: newData?.business_name
        }),
        ...(newData?.business_address !==
          transactionStateData?.business_address && {
          business_address: newData?.business_address
        }),
        ...(newData?.tin !== transactionStateData?.tin && {
          tin: newData?.tin
        }),
        ...(newData?.terms !== transactionStateData?.terms && {
          terms: newData?.terms
        })
      }
    });

    await getTransactionDetails(transactionStateData?.transaction_no);
    setTransactionStateData(newData);
  };

  const onSalesInvoicePriceToggle = (v: boolean) => {
    setIsSIPricing(v);

    const clonedTransactionState = cloneDeep(transactionStateData);
    // let adjustedSIItems = clonedTransactionState?.items || [];

    if (v) {
      setPrevTransactionState(transactionStateData);
      clonedTransactionState?.items?.map((x: Transaction) => {
        x.amount = promoPriceToSRP(x.amount);
        x.retail_price = promoPriceToSRP(x.retail_price);
      });
      // setTransactionStateData(clonedTransactionState);
      setTransactionStateData((prev) => ({
        ...prev,
        items: prev?.items?.map((x) => {
          const foo = {
            ...x,
            /* Show retail price if amount is unavailable in table cell below for items with quantity by 
            meters eg. rj45 w/ quantity 3 meters because retail price is manually set by operations regardless of meters */
            amount: promoPriceToSRP(x.amount || x.retail_price),
            retail_price: promoPriceToSRP(x.retail_price)
          };
          return foo;
        })
      }));
    } else {
      if (prevTransactionState) {
        setTransactionStateData(prevTransactionState);
      }
    }
  };

  const onUpdateOfRmaItems = (newItems: Transaction[]) => {
    setTransactionStateData((prev) => ({ ...prev, items: newItems }));
  };

  const transactionItems = useMemo(() => {
    let clonedTransactionStateData = cloneDeep(transactionStateData);
    if (clonedTransactionStateData) {
      clonedTransactionStateData.items = clonedTransactionStateData.items?.filter(
        (x) => x?.rma?.is_hidden_print === false || !x?.rma
      );
    }
    const arr: Transaction[] = clonedTransactionStateData?.items || [];
    const reducedItems: Transaction[] = [];
    arr.forEach((item) => {
      const existingIndex = reducedItems?.findIndex(
        (x) =>
          x?.product_name == item?.product_name &&
          // also check price because some products are same but have different retails price
          x?.retail_price === item?.retail_price
      );
      if (existingIndex > -1) {
        reducedItems[existingIndex].quantity =
          (reducedItems[existingIndex].quantity || 0) + (item?.quantity || 0);
        reducedItems[existingIndex].amount =
          (reducedItems[existingIndex].amount || 0) + (item?.amount || 0);

        if (item?.serial_no) {
          // Check is it a bundle item with bundle items that has serial nos,
          // if yes, we need to also add the serial nos of the bundle items to the main item in order for it to be printed in receipt
          if (item?.is_product_bundle && item?.bundle_items && item?.bundle_items?.length > 0) {
            const mappedBundleItemsSerials: string[] = item?.bundle_items?.map(bundleItem => bundleItem?.serial_no || '')?.filter(serial => !!serial) || [];
            reducedItems[existingIndex].serial_nos?.push(...mappedBundleItemsSerials);
          } else {
            reducedItems[existingIndex].serial_nos?.push(item?.serial_no);
          }
        }
      } else {
        if (item.serial_no) {
          if (item?.is_product_bundle && item?.bundle_items && item?.bundle_items?.length > 0) {
            const mappedBundleItemsSerials: string[] = item?.bundle_items?.map(bundleItem => bundleItem?.serial_no || '')?.filter(serial => !!serial) || [];
            item.serial_nos = [item.serial_no, ...mappedBundleItemsSerials];
          } else {
            item.serial_nos = [item.serial_no];
          }
        }
        reducedItems.push(item);
      }
    });
    return reducedItems;
  }, [transactionStateData]);

  const totalPriceOnPrint = useMemo(() => {
    const totalKachiPoints = Math.abs(transactionStateData?.kachi_points || 0);
    const totalVoucherDiscount = Math.abs(
      anyToNumber(transactionStateData?.voucher_discount || 0)
    );
    const total = transactionStateData?.items?.reduce((accumulator, item) => {
      let toBeAdded: number = 0;
      if (
        item?.rma?.is_hidden_print ||
        (item?.rma && item?.rma?.is_hidden_print === undefined)
      ) {
        toBeAdded = 0;
      } else {
        toBeAdded = getNumberOrZero(item?.amount);
      }
      return accumulator + toBeAdded;
    }, 0);
    const newTotal = (total || 0) - totalKachiPoints - totalVoucherDiscount;

    return newTotal;
  }, [transactionStateData]);

  //This is complicated logic I vibe code it pardon me :< but you can have props manipulation in transactionStateData
  const pagesData = useMemo(() => {
    const limitOfLinePerPage = 22; // max lines per page
    const characterPerLine = 43; // chars before product name wraps
    let totalLinesAccumulated = 0;

    const startNewPage = () => {
      pageData.push({
        pageNo: pageData.length + 1,
        page: { ...transactionStateData, items: [] }
      });
      totalLinesAccumulated = 0;
    };

    let pageData: TransactionPrintPageData[] = [
      {
        pageNo: 1,
        page: { ...transactionStateData, items: [] }
      }
    ];

    const clonedTransactionItems = cloneDeep(transactionItems);

    clonedTransactionItems.forEach((item, itemIndex) => {
      const name = item.product_name || '';
      const nameLength = name.length || 0;
      const linesTakenByName = Math.ceil(nameLength / characterPerLine);
      const linesTakenByWarranty = 1;
      const baseLines = linesTakenByName + linesTakenByWarranty; // printed only once per product

      const isLastItem = itemIndex === clonedTransactionItems.length - 1;
      const extraLines =
        (transactionStateData?.kachi_points ? 1 : 0) +
        (transactionStateData?.voucher_discount ? 1 : 0);

      // filter serials (your existing rules)
      const filteredSerial =
        item.serial_nos?.filter((serial) => {
          const isValidSerial =
            item?.product_type !== ProductTypeEnum.Consumable &&
            item?.serial_nos &&
            item?.serial_nos?.length > 0;
          const isSerializedItem =
            !/^SN-\d{13}$/.test(serial) && !serial.includes('PCWSN-');
          return isValidSerial && isSerializedItem;
        }) || [];

      let serialIndex = 0;
      let needsBase = true; // only first chunk prints product name + warranty

      // Ensure at least one iteration even if there are no serials
      while (
        serialIndex < filteredSerial.length ||
        (filteredSerial.length === 0 && needsBase)
      ) {
        // If current page is full, start a new one
        if (totalLinesAccumulated >= limitOfLinePerPage) {
          startNewPage();
        }

        const currentPage = pageData[pageData.length - 1];
        // make sure items exists (TS-safe)
        currentPage.page.items = currentPage.page.items ?? [];

        const linesLeft = limitOfLinePerPage - totalLinesAccumulated;

        // If we need to print the base block and it doesn't fit, move to a new page
        if (needsBase && baseLines > linesLeft) {
          startNewPage();
          continue;
        }

        const remainingSerials = filteredSerial.length - serialIndex;
        const baseCost = needsBase ? baseLines : 0;

        // Max serials we could fit on this page *before* reserving final extras
        let capacityForSerials = Math.max(0, linesLeft - baseCost);

        // Decide if this chunk would be the last chunk for the last item
        const wouldBeLastChunkIfAllFit = remainingSerials <= capacityForSerials;
        let reserveExtrasThisChunk = false;

        if (isLastItem && wouldBeLastChunkIfAllFit) {
          // We must reserve space for totals (kachi_points/voucher) on the same final page
          // Reduce serial capacity to leave room for those extra lines
          const adjustedCapacity = Math.max(0, capacityForSerials - extraLines);

          // If even base + extras alone can't fit, start a new page and try again
          if (needsBase && baseCost + extraLines > linesLeft) {
            startNewPage();
            continue;
          }

          capacityForSerials = adjustedCapacity;
          reserveExtrasThisChunk = true;
        }

        // If there are no serials at all for this item
        if (filteredSerial.length === 0) {
          // Still need to consider extras on last item
          let linesThisChunk = baseCost + (isLastItem ? extraLines : 0);
          if (linesThisChunk > linesLeft) {
            startNewPage();
            continue;
          }

          const updatedItem = { ...item, serial_nos: [] };
          currentPage.page.items.push(updatedItem);
          totalLinesAccumulated += linesThisChunk;
          needsBase = false;
          break; // done with this item
        }

        // If there's no room for any serials on this page, start a new page
        if (capacityForSerials <= 0) {
          startNewPage();
          continue;
        }

        const chunkSize = Math.min(remainingSerials, capacityForSerials);
        const serialChunk = filteredSerial.slice(
          serialIndex,
          serialIndex + chunkSize
        );

        // lines consumed by this chunk
        let linesThisChunk =
          baseCost +
          serialChunk.length +
          (reserveExtrasThisChunk ? extraLines : 0);

        // Safety: if still doesn't fit (shouldn't happen, but guard anyway), new page
        if (linesThisChunk > linesLeft) {
          startNewPage();
          continue;
        }

        // Push chunk
        const updatedItem = {
          ...item,
          serial_nos: serialChunk
          // Optional: add a marker for continued items
          // __continued: !needsBase
        };

        currentPage.page.items.push(updatedItem);
        totalLinesAccumulated += linesThisChunk;

        serialIndex += chunkSize;
        needsBase = false;

        // If we placed the last serials, we're done with this item
        if (serialIndex >= filteredSerial.length) {
          break;
        }
      }
    });

    const finalData = transactionDataCleaner(pageData);
    return finalData;
  }, [transactionItems, transactionStateData]);

  /** Split a string into array of string (string[]) depending how long. **/
  const splitDescription = (textArg: string, limit = 67) => {
    const text = textArg.trim();
    const result = [];
    let i = 0;
    while (i < text.length) {
      // Slice from i up to limit chars
      const chunk = text.slice(i, i + limit);
      result.push(chunk);
      i += limit;
    }
    return result;
  };

  const looseLeafPageData = useMemo(() => {
    const limitOfCharacterPerLineOfDescription = 73;

    // Creating a whole page data. because later, we'll only
    const wholePageData: TransactionPrintPageData[] = [
      {
        pageNo: 0,
        page: {
          ...transactionStateData,
          items: []
        }
      }
    ];

    // Cloning transactionItems to avoid mutation
    const clonedTransactionItems = cloneDeep(transactionItems);

    // For Kachi Points Logic. We need to
    const kachiPointsTitle = 'KACHI POINTS USED:';
    const kachiPoints = transactionStateData?.kachi_points;
    const hasKachiPoints = kachiPoints !== undefined && kachiPoints !== 0;
    if (hasKachiPoints) {
      wholePageData[0].page.items?.push({
        ll_description: `${kachiPointsTitle}`,
        ll_amount: Math.abs(kachiPoints)
      });
    }

    // For Voucher Logic
    const voucherDiscountTitle = 'VOUCHER CODE:';
    const voucherDiscount = Number(transactionStateData?.voucher_discount);
    const voucher_code = transactionStateData?.voucher_code;
    const hasVoucherDiscount =
      voucherDiscount !== undefined && voucherDiscount !== 0;
    if (hasVoucherDiscount) {
      wholePageData[0].page.items?.push({
        ll_description: `${voucherDiscountTitle} ${voucher_code}`,
        ll_amount: Math.abs(voucherDiscount)
      });
    }

    console.log('clonedTransactionItems', clonedTransactionItems);


    // Looping transactionItems to get line by line details
    clonedTransactionItems?.forEach((transactionItem) => {
      // Splitting product_name for it to have dynamic next lines.
      // splitDescription(`${transactionItem?.product_name}RADEON RX 7600 ASUS DUAL EVO OC EDITION BLACK 8GB GDDR6 DUAL FAN 7600 ASUS DUAL EVO OC EDITION` || '', limitOfCharacterPerLineOfDescription)
      splitDescription(
        `${transactionItem?.product_name}` || '',
        limitOfCharacterPerLineOfDescription
      )?.forEach((desc, indexDescription) => {
        const firstLine = indexDescription === 0;

        // Only insert the qty, unit per cost and amount if first in line..
        if (firstLine) {
          wholePageData[0].page.items?.push({
            ll_description: desc?.trim(),
            ll_quantity: transactionItem?.quantity || 0,
            ll_unit_per_cost: transactionItem?.retail_price,
            ll_amount: transactionItem?.amount
          });
        } else {
          // If not first in line. Only add "description"..
          wholePageData[0].page.items?.push({ ll_description: desc?.trim() });
        }
      });

      // filter serial nos that we want in receipt
      const filteredSerial =
        transactionItem?.serial_nos?.filter((serial: string) => {
          // check serial conditions
          const isValidSerial =
            transactionItem?.product_type !== ProductTypeEnum.Consumable &&
            transactionItem?.serial_nos &&
            transactionItem?.serial_nos?.length > 0;
          // do not allow non serialized items
          const IsSerializeditem =
            !/^SN-\d{13}$/?.test(serial) && !serial?.includes('PCWSN-');

          const serialConditions = [isValidSerial, IsSerializeditem];

          return serialConditions.every((condition) => !!condition);
        }) || [];

      // Join Serial No(s)
      const joinedSerialNosAsString = (filteredSerial || [])?.join(',');

      // Insert lines of joined Serial No(s) as a whole string so it may be divided per line as well
      splitDescription(
        `${joinedSerialNosAsString}` || '',
        limitOfCharacterPerLineOfDescription
      )?.forEach((descSerial) => {
        wholePageData[0].page.items?.push({
          ll_description: descSerial?.trim()
        });
      });

      // Finding warranty duration to be used later.
      if (transactionItem?.warranty_duration) {
        // Finding warranty duration to be used later.
        const warranyDuration = warranties?.find((warranty) => {
          return warranty?.value === transactionItem?.warranty_duration;
        });
        if (warranyDuration) {
          // Insert Warranty Duration of product
          wholePageData[0].page.items?.push({
            ll_description: `WARRANTY: ${warranyDuration?.name}`
          });
        }
      }

      // If Product is Laptop (Category Id 39). Insert Battery Warranty Line
      if (transactionItem?.category_id === 39) {
        wholePageData[0].page.items?.push({
          ll_description: `BATTERY WARRANTY: 1 YEAR - (365 DAYS)`
        });
      }
      
      //If the product is a bundle
      if (transactionItem.bundle_items != undefined) {
        transactionItem.bundle_items.forEach((item) => {
          wholePageData[0].page.items?.push({
            ll_description: item.product_name,
            ll_quantity: 1,
            ll_unit_per_cost: 0.00,
            ll_amount: 0.00
          })
          wholePageData[0].page.items?.push({
            ll_description: item.serial_no
          })
          if (transactionItem?.warranty_duration) {
            // Finding warranty duration to be used later.
            const warranyDuration = warranties?.find((warranty) => {
              return warranty?.value === item?.warranty_duration || 0;
            });
            if (warranyDuration) {
              // Insert Warranty Duration of product
              wholePageData[0].page.items?.push({
                ll_description: `WARRANTY: ${warranyDuration?.name}`
              });
            }
          }
        })
      }
    });

    // Insert here logic for last page¸ last lines
    const lengthOfItemsBeforeFinalsLines =
      wholePageData[0]?.page?.items?.length || 0;
    if (lengthOfItemsBeforeFinalsLines > 0) {
      // Insert here logic for last page¸ last lines
      // Of Course, The "Nothing Follows" Line. This is important to loose leaf receipts. To avoid scams.
      wholePageData[0].page.items?.push({
        ll_description: `------------------------ Nothing Follows ------------------------`
      });
    }

    // for (let i = 0; i < 20; i++) {
    //   wholePageData[0].page.items?.push({
    //     product_id: 12,
    //     ll_quantity: 999,
    //     // title: transactionItem?.ll_description,
    //     ll_description: 'WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW',
    //     // ll: 'WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW',
    //     ll_unit_per_cost: 999999,
    //     ll_amount: 999999,
    //     // spend_kachi_points: x?.spend_kachi_points
    //   });
    // }

    return wholePageData;
  }, [transactionItems, transactionStateData]);

  //TODO: this is the old version of pagesData retain for reference improvement
  //Tap me Angelo Dela Cruz if you have concern or question on this
  // const pagesData = useMemo(() => {
  //   const limitOfLinePerPage = 22; // hard coded optimal number by manually counting lines before overflow occurs
  //   let totalLinesAccumulated = 0;

  //   let pageData: TransactionPrintPageData[] = [
  //     {
  //       pageNo: 1,
  //       page: {
  //         ...transactionStateData,
  //         items: []
  //       }
  //     }
  //   ];

  //   const clonedTransactionItems = cloneDeep(transactionItems);
  //   // console.table(clonedTransactionItems);

  //   clonedTransactionItems.forEach((i, ind) => {
  //     const name = i.product_name || '';
  //     const characterPerLine = 43; // no. of characters the productname cell can fit before wrapping to next line
  //     const filteredSerial =
  //       i.serial_nos?.filter((serial) => {
  //         // check serial conditions
  //         const isValidSerial =
  //           i?.product_type !== ProductTypeEnum.Consumable &&
  //           i?.serial_nos &&
  //           i?.serial_nos?.length > 0;
  //         // do not allow non serialized items
  //         const IsSerializeditem =
  //           !/^SN-\d{13}$/?.test(serial) && !serial?.includes('PCWSN-');

  //         const serialConditions = [isValidSerial, IsSerializeditem];

  //         return serialConditions.every((condition) => !!condition);
  //       }) || [];

  //     console.info(`test serialize ${JSON.stringify(filteredSerial)}`);

  //     const linesTakenBySerial = filteredSerial.length || 0;
  //     const pageNoBasedOnLength = pageData?.length;
  //     const lastIndexOfPageData = pageNoBasedOnLength - 1;
  //     const isLastPage = ind === clonedTransactionItems.length - 1;
  //     const nameLength = name.length || 0;
  //     const linesTakenByName = Math.ceil(nameLength / characterPerLine);
  //     const linesTakeByWarranty = 1;
  //     let linesTakenByItem =
  //       linesTakenByName + linesTakeByWarranty + linesTakenBySerial;

  //     if (transactionStateData?.kachi_points !== 0 && isLastPage) {
  //       linesTakenByItem++; // add kachi points to line consideration if its the last item
  //     }

  //     if (transactionStateData?.voucher_discount !== 0 && isLastPage) {
  //       linesTakenByItem++;
  //     }

  //     const updatedItem = { ...i, serial_nos: filteredSerial };

  //     if (totalLinesAccumulated + linesTakenByItem <= limitOfLinePerPage) {
  //       // @ts-ignore: Object is possibly 'null'. Intended.
  //       pageData[lastIndexOfPageData]?.page.items.push(updatedItem);
  //       totalLinesAccumulated += linesTakenByItem;
  //     } else {
  //       totalLinesAccumulated = 0; // reset total lines val and re add new one
  //       totalLinesAccumulated += linesTakenByItem;
  //       pageData.push({
  //         pageNo: pageNoBasedOnLength + 1,
  //         page: {
  //           ...transactionStateData,
  //           items: [updatedItem]
  //         }
  //       });
  //     }
  //   });

  //   const finalData = transactionDataCleaner(pageData);
  //   return finalData;
  // }, [transactionItems, transactionStateData]);

  const releasedBy = useMemo(() => {
    const elementWithName = transactionStateData?.items?.find(
      (x) => typeof x.released_by_name !== 'undefined'
    );
    // const arrName = elementWithName?.released_by_name;
    let firstAndInitialLastName = initializedWordsOtherThanFirst(
      elementWithName?.released_by_name || ''
    );
    if (isEmptyOrSpaces(firstAndInitialLastName)) {
      firstAndInitialLastName = 'PC Worth';
    }
    return `${elementWithName?.created_by || 0}-${firstAndInitialLastName}`;
  }, [transactionStateData]);

  const hasRMA = useMemo(
    () => transactionStateData?.items?.filter((x) => x.rma)?.length,
    [transactionStateData]
  );

  const storeBranch = useMemo(() => {
    const commonBranchName = branchIDToName(
      getMostCommonBranchIdOnTransactionItems(transactionStateData?.items)
    );
    return commonBranchName || '';
  }, [branchIDToName, transactionStateData]);

  const orderNoOverrideOnURL = useMemo(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const orderNo = urlParams.get('on');
    return orderNo ? orderNo : undefined;
  }, []);

  // const claimStubDataMemoized  = useMemo(() => {
  //   // console.log('claimStubData transactionStateData', transactionStateData);
  //   // console.log('claimStubData transactionItems', transactionItems);
  //   const clonedTransactionItems = cloneDeep(transactionItems);
  //   const clonedPaymentsResp = cloneDeep(transactionStateData?.paymentsResponse);

  //   const claimStubItems: any[] = [];
  //   clonedTransactionItems?.forEach(item => {
  //     // claimStubItems.push({ type: 'raw', format: 'command', data: formatLine(item?.quantity, item?.product_name, item?.retail_price) });
  //     claimStubItems.push({ type: 'raw', format: 'command', data: `${item?.quantity} x ${item?.product_name}\n` });
  //     claimStubItems.push({ type: 'raw', format: 'command', data: `PHP ${formatCurrency(item?.retail_price, 'PHP', 2, {removeCurrency: true})}\n\n` });
  //   });

  //   // Payments Section
  //   // const claimStubPaymentsResp = transactionStateData?.paymentsResponse;

  //TODO: leave this empty because of TS error
  let claimStubPayments: any[] = [];
  //   const filteredPayments = clonedPaymentsResp?.data?.filter(payment => {
  //     // Exclude hidden payments
  //     return !payment?.deleted_by;
  //   }) || [];

  //   filteredPayments?.forEach(payment => {
  //     claimStubPayments.push({ type: 'raw', format: 'command', data: `${payment?.payment_type}: PHP ${formatCurrency(payment?.amount, 'PHP', 2, {removeCurrency: true})}\n` });
  //   });

  //   // TODO: Add Logic Of Voucher and Kachi Points.
  //   // For Kachi Points Logic. We need to
  //   const kachiPointsTitle = 'KACHI POINTS USED:';
  //   const kachiPoints = transactionStateData?.kachi_points
  //   const hasKachiPoints = kachiPoints !== undefined && kachiPoints !== 0;
  //   if (hasKachiPoints) {
  //       claimStubItems.push({ type: 'raw', format: 'command', data: `${kachiPointsTitle}\n` });
  //       claimStubItems.push({ type: 'raw', format: 'command', data: `-PHP ${formatCurrency(kachiPoints, 'PHP', 2, {removeCurrency: true})}\n\n` });
  //     }

  //   // For Voucher Logic
  //   const voucherDiscountTitle = 'VOUCHER CODE:';
  //   const voucherDiscount = transactionStateData?.voucher_discount
  //   const voucher_code =  transactionStateData?.voucher_code
  //   const hasVoucherDiscount =
  //   voucherDiscount !== undefined && voucherDiscount !== 0;
  //   if (hasVoucherDiscount) {
  //       claimStubItems.push({ type: 'raw', format: 'command', data: `${voucherDiscountTitle} ${voucher_code}\n` });
  //       claimStubItems.push({ type: 'raw', format: 'command', data: `-PHP ${formatCurrency(voucherDiscount, 'PHP', 2, {removeCurrency: true})}\n\n` });
  //   }

  //   const finalClaimStubPayload: PrintData[] = [
  //     // Store header (center, bold, double height)
  //     { type: 'raw', format: 'command', data: '\x1B\x61\x01' }, // Center align
  //     { type: 'raw', format: 'command', data: '\x1B\x21\x30' }, // Bold + double height
  //     { type: 'raw', format: 'command', data: '! CLAIM STUB !\n' },
  //     { type: 'raw', format: 'command', data: 'PC Worth\n' },
  //     { type: 'raw', format: 'command', data: '\x1B\x21\x00' }, // Reset style
  //     { type: 'raw', format: 'command', data: `${storeBranch}\n` },

  //     // Left align details
  //     { type: 'raw', format: 'command', data: '\x1B\x61\x00' }, // Left align
  //     { type: 'raw', format: 'command', data: `Transaction No: ${transactionStateData?.transaction_no}\n` },
  //     { type: 'raw', format: 'command', data: `Order No: ${transactionStateData?.order_no}\n` },
  //     { type: 'raw', format: 'command', data: `Date: ${dateToday()}\n` },
  //     { type: 'raw', format: 'command', data: `Cashier: ${userDetails?.first_name || ''}\n` },
  //     { type: 'raw', format: 'command', data: '-----------------------------------\n' },

  //     ...claimStubItems,
  //     { type: 'raw', format: 'command', data: '-----------------------------------\n' },

  //     // Total (bold)
  //     { type: 'raw', format: 'command', data: '\x1B\x45\x01' }, // Bold on
  //     { type: 'raw', format: 'command', data: `Total Amount: PHP ${formatCurrency(transactionStateData?.total_price, 'PHP', 2, {removeCurrency: true})}\n` },
  //     { type: 'raw', format: 'command', data: `Balance: PHP ${formatCurrency(clonedPaymentsResp?.total_balance, 'PHP', 2, {removeCurrency: true})}\n\n` },
  //     { type: 'raw', format: 'command', data: '\x1B\x45\x00' }, // Bold off
  //     ...claimStubPayments,
  //     { type: 'raw', format: 'command', data: '-----------------------------------\n' },
  //     // Customer Information (bold)

  //     // Footer (center align)
  //     { type: 'raw', format: 'command', data: '\x1B\x61\x01' }, // Center align
  //     { type: 'raw', format: 'command', data: 'THIS IS NOT A SALES INVOICE\n' },
  //     { type: 'raw', format: 'command', data: 'The official invoice will be provided once\n' },
  //     { type: 'raw', format: 'command', data: 'your item(s) are released and tested good.\n\n' },
  //     { type: 'raw', format: 'command', data: 'Thank you for your patience!\n' },
  //     { type: 'raw', format: 'command', data: 'Please keep this claim stub.\n\n\n\n\n' },

  //     // Cut
  //     { type: 'raw', format: 'command', data: '\x1D\x56\x00' }
  //   ];

  //   return finalClaimStubPayload;
  // }, [storeBranch, transactionItems, transactionStateData, userDetails]);

  const generateClaimStubData = (modeOfTransaction: string) => {
    // console.log('claimStubData transactionStateData', transactionStateData);
    // console.log('claimStubData transactionItems', transactionItems);

    const clonedTransactionItems = cloneDeep(transactionItems);
    const clonedPaymentsResp = cloneDeep(
      transactionStateData?.paymentsResponse
    );

    const claimStubDataReadable = {
      store_branch: storeBranch,
      transaction_no: transactionStateData?.transaction_no,
      order_no: transactionStateData?.order_no,
      date_today: dateToday(),
      cashier_name: userDetails?.first_name || '',
      items: [{}],

      total_amount: transactionStateData?.total_price,
      balance: clonedPaymentsResp?.total_balance,
      payments: [{}],
      kachi_points: '',
      voucher: ''

      // claimStubTotals: [
      //       { type: 'raw', format: 'command', data: `Total Amount: PHP ${formatCurrency(transactionStateData?.total_price, 'PHP', 2, {removeCurrency: true})}\n` },
      // { type: 'raw', format: 'command', data: `Balance: PHP ${formatCurrency(clonedPaymentsResp?.total_balance, 'PHP', 2, {removeCurrency: true})}\n\n` },
    };

    claimStubDataReadable.items = [];
    claimStubDataReadable.payments = [];

    const claimStubItems: any[] = [];
    clonedTransactionItems?.forEach((item) => {
      // claimStubItems.push({ type: 'raw', format: 'command', data: formatLine(item?.quantity, item?.product_name, item?.retail_price) });
      claimStubItems.push({
        type: 'raw',
        format: 'command',
        data: `${item?.quantity} x ${item?.product_name}\n`
      });
      claimStubItems.push({
        type: 'raw',
        format: 'command',
        data: `PHP ${formatCurrency(item?.retail_price, 'PHP', 2, {
          removeCurrency: true
        })}\n\n`
      });
      claimStubDataReadable.items.push({
        quantity: item?.quantity,
        product_name: item?.product_name,
        retail_price: item?.retail_price
      });
    });

    // Payments Section
    // const claimStubPaymentsResp = transactionStateData?.paymentsResponse;

    //TODO: remove payment on stub
    // let claimStubPayments: any[] = [];
    // const filteredPayments =
    //   clonedPaymentsResp?.data?.filter((payment) => {
    //     // Exclude hidden payments
    //     return !payment?.deleted_by;
    //   }) || [];

    // filteredPayments?.forEach((payment) => {
    //   claimStubPayments.push({
    //     type: 'raw',
    //     format: 'command',
    //     data: `${payment?.payment_type}: PHP ${formatCurrency(
    //       payment?.amount,
    //       'PHP',
    //       2,
    //       { removeCurrency: true }
    //     )}\n`
    //   });
    //   claimStubDataReadable.payments.push({
    //     payment_type: payment?.payment_type,
    //     amount: payment?.amount
    //   });
    // });

    const kachiPointsTitle = 'KACHI POINTS USED:';
    const kachiPoints = transactionStateData?.kachi_points;
    const hasKachiPoints = kachiPoints !== undefined && kachiPoints !== 0;
    if (hasKachiPoints) {
      claimStubItems.push({
        type: 'raw',
        format: 'command',
        data: `${kachiPointsTitle}\n`
      });
      claimStubItems.push({
        type: 'raw',
        format: 'command',
        data: `-PHP ${formatCurrency(kachiPoints, 'PHP', 2, {
          removeCurrency: true
        })}\n\n`
      });
      claimStubDataReadable.kachi_points = `-PHP ${formatCurrency(
        kachiPoints,
        'PHP',
        2,
        { removeCurrency: true }
      )}`;
    }

    // For Voucher Logic
    const voucherDiscountTitle = 'VOUCHER CODE:';
    const voucherDiscount = Number(transactionStateData?.voucher_discount);
    const voucher_code = transactionStateData?.voucher_code;
    const hasVoucherDiscount =
      voucherDiscount !== undefined && voucherDiscount !== 0;
    if (hasVoucherDiscount) {
      claimStubItems.push({
        type: 'raw',
        format: 'command',
        data: `${voucherDiscountTitle} ${voucher_code}\n`
      });
      claimStubItems.push({
        type: 'raw',
        format: 'command',
        data: `-PHP ${formatCurrency(voucherDiscount, 'PHP', 2, {
          removeCurrency: true
        })}\n\n`
      });
      claimStubDataReadable.voucher = `${voucherDiscountTitle} ${voucher_code} -PHP ${formatCurrency(
        voucherDiscount,
        'PHP',
        2,
        { removeCurrency: true }
      )}`;
    }

    const finalClaimStubPayload: PrintData[] = [
      // Store header (center, bold, double height)
      { type: 'raw', format: 'command', data: '\x1B\x61\x01' }, // Center align
      { type: 'raw', format: 'command', data: '\x1B\x21\x30' }, // Bold + double height
      { type: 'raw', format: 'command', data: '! CLAIM STUB !\n' },
      { type: 'raw', format: 'command', data: 'PC Worth\n' },
      { type: 'raw', format: 'command', data: '\x1B\x21\x10' }, // double height
      {
        type: 'raw',
        format: 'command',
        data: `${storeBranch?.toUpperCase()}\n`
      },
      { type: 'raw', format: 'command', data: '\x1B\x21\x00' }, // reset style

      // Left align details
      { type: 'raw', format: 'command', data: '\x1B\x61\x00' }, // Left align
      { type: 'raw', format: 'command', data: `Date: ${dateToday()}\n` },
      {
        type: 'raw',
        format: 'command',
        data: `Customer's Name: ${transactionStateData?.customer?.first_name} ${transactionStateData?.customer?.last_name}\n`
      },
      {
        type: 'raw',
        format: 'command',
        data: `Transaction No: ${transactionStateData?.transaction_no}\n`
      },
      {
        type: 'raw',
        format: 'command',
        data: `Order No: ${transactionStateData?.order_no}\n`
      },
      {
        type: 'raw',
        format: 'command',
        data: `MOP: ${transactionStateData?.payment_mode || ''}\n`
      },
      {
        type: 'raw',
        format: 'command',
        data: `MOT: ${modeOfTransaction || ''}\n`
      },
      // { type: 'raw', format: 'command', data: `Date: ${dateToday()}\n` },
      // {
      //   type: 'raw',
      //   format: 'command',
      //   data: `Cashier: ${userDetails?.first_name || ''}\n`
      // },
      {
        type: 'raw',
        format: 'command',
        data: '-----------------------------------\n'
      },
      //TODO: this was removed for stub
      // ...claimStubItems,
      // {
      //   type: 'raw',
      //   format: 'command',
      //   data: '-----------------------------------\n'
      // },

      // Total (bold)
      //TODO: temporarily removed based on accounting needs
      // { type: 'raw', format: 'command', data: '\x1B\x45\x01' }, // Bold on
      // {
      //   type: 'raw',
      //   format: 'command',
      //   data: `Total Amount: PHP ${formatCurrency(
      //     transactionStateData?.total_price,
      //     'PHP',
      //     2,
      //     { removeCurrency: true }
      //   )}\n`
      // },
      // {
      //   type: 'raw',
      //   format: 'command',
      //   data: `Balance: PHP ${formatCurrency(
      //     clonedPaymentsResp?.total_balance,
      //     'PHP',
      //     2,
      //     { removeCurrency: true }
      //   )}\n\n`
      // },
      // { type: 'raw', format: 'command', data: '\x1B\x45\x00' }, // Bold off
      ...claimStubPayments,
      // {
      //   type: 'raw',
      //   format: 'command',
      //   data: '-----------------------------------\n'
      // },
      // Customer Information (bold)

      // Footer (center align)
      { type: 'raw', format: 'command', data: '\x1B\x61\x01' }, // Center align
      { type: 'raw', format: 'command', data: 'THIS IS NOT A SALES INVOICE\n' },
      {
        type: 'raw',
        format: 'command',
        data: 'The official invoice will be provided once\n'
      },
      {
        type: 'raw',
        format: 'command',
        data: 'your item(s) are released and tested good.\n\n'
      },
      {
        type: 'raw',
        format: 'command',
        data: 'Thank you for your patience!\n'
      },
      {
        type: 'raw',
        format: 'command',
        data: 'Please keep this claim stub.\n\n\n\n\n'
      },

      // Cut
      { type: 'raw', format: 'command', data: '\x1D\x56\x00' }
    ];

    return { finalClaimStubPayload, claimStubDataReadable };
  };

  const printClaimStub = async (modeOfTransfer: string) => {
    // Save to supabase that claim stub is printed (timestamp and who printed it)
    const {
      claimStubDataReadable,
      finalClaimStubPayload
    } = generateClaimStubData(modeOfTransfer);

    const claimStubLogData = {
      internal_transaction_no: transactionStateData?.transaction_no,
      internal_created_by:
        userDetails?.first_name + ' ' + userDetails?.last_name,
      claim_stub_data: JSON.stringify(finalClaimStubPayload), // String because Supabase doesnt support "\\u0000 cannot be converted to text.",
      claim_stub_data_readable: claimStubDataReadable // String because Supabase doesnt support "\\u0000 cannot be converted to text.",
    };
    // generate print data here then save to supabase database
    const { error } = await SupabaseClientService.from(
      'claim_stub_logs'
    ).insert({ ...claimStubLogData });
    console.log(claimStubLogData, error);
    console.error(error);

    qz.websocket
      .connect()
      .then(() => {
        return qz.printers.find();
      })
      .then((printers) => {
        let targetPrinter;
        console.log('Available printers:', printers);
        if (Array.isArray(printers)) {
          // targetPrinter = printers?.find((p) => p.includes('T82X')); // flexible match
          targetPrinter = printers?.find((p) => {
            const lower = p.toLowerCase();
            return lower.includes('t82x') || lower.includes('tm-t');
          });
        } else if (typeof printers === 'string') {
          targetPrinter = printers.includes('T82X') ? printers : undefined;
        }

        if (!targetPrinter) {
          throw new Error('EPSON TM-T82X not found');
        }

        const config = qz.configs.create(targetPrinter);

        console.log('Using printer:', targetPrinter);
        console.log('config:', config);
        console.log('claimStubData:', finalClaimStubPayload);
        console.log('claimStubDataReadable:', claimStubDataReadable);

        return qz.print(config, finalClaimStubPayload);
      })
      .catch((e) => {
        console.error(e);
        alert('Claim Stub Print Error: ' + (e?.message || e));
      })
      .finally(() => qz.websocket.disconnect());
  };

  const onLooseLeafClickHistoryItemClicked = (item: LooseLeafHistory) => {
    // alert(`Confidential: Ask IT for this.\n\nBy: ${item?.internal_created_by}\nAt: ${formatDate(item.created_at, 'PPpp')}`);

    // Check if 2307 or not
    setLooseLeafHistoryPreviewData(item);
  };

  useEffect(() => {
    getTransactionDetails(sessionTransaction?.transaction_no);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO: Improve this logic, by centralizing the source of truth of transaction state data
  useEffect(() => {
    if (transactionDetailsResponse) {
      const normalized = {
        ...sessionTransaction,
        ...transactionDetailsResponse.data,
        business_name: transactionDetailsResponse?.data?.business_name,
        business_address: transactionDetailsResponse?.data?.business_address,
        tin: transactionDetailsResponse?.data?.tin,
        terms: transactionDetailsResponse?.data?.terms,
        voucher_code:
          transactionDetailsResponse?.data?.voucher?.code ??
          sessionTransaction?.voucher_code,
        voucher_discount:
          (Number(sessionTransaction?.voucher_discount) || 0) ??
          (Number(transactionDetailsResponse?.data?.voucher?.amount) || 0)
      };
      setTransactionStateData(normalized);
    }
  }, [transactionDetailsResponse]);

  useEffect(() => {
    // Get the state from sessionStorage
    if (!transactionStateData) {
      onLooseleafClickHistoryFetch(sessionTransaction?.transaction_no);
      onClaimStubClickHistoryFetch(sessionTransaction?.transaction_no);
    }
  }, [transactionStateData, orderNoOverrideOnURL]);

  useEffect(() => {
    if (transactionStateData && orderNoOverrideOnURL && !isPrintOpen) {
      setIsPrintOpen(true);
    }
  }, [transactionStateData, orderNoOverrideOnURL, isPrintOpen]);

  //TODO: this is temp solution
  useEffect(() => {
    fetchTapTapCustomerDetails(sessionTransaction?.customer?.id);
  }, [sessionTransaction.customer.id]);

  return (
    <div>
      <RenderDetailEditor
        looseLeafBusinessInfo={looseLeafBusinessInfo}
        setLooseLeafBusinessInfo={(val) => setLooseLeafBusinessInfo(val)}
        isSIPricing={isSIPricing}
        transactionDetails={transactionStateData}
        onUpdateOfTransaction={onUpdateOfTransaction}
        onSalesInvoicePriceToggle={onSalesInvoicePriceToggle}
        isBlankPage={isBlankPage}
        toggleBlankPage={() => setIsBlankPage(!isBlankPage)}
        onClaimStubPrint={() =>
          printClaimStub(taptapCustomer[0]?.courier || '')
        }
        togglePrintOpen={() => setIsPrintOpen(!isPrintOpen)}
        toggleLooseLeafPrintOpen={() => {
          if (
            (looseLeafPageData?.[0]?.page?.items?.length || 0) >
            linesLimitPerPageOnLooseLeaf
          ) {
            alert(
              `Loose Leaf Receipt can only contain up to ${linesLimitPerPageOnLooseLeaf} lines of items. Please DIVIDE transaction items to fit the said limit.`
            );
            return;
          }
          onLooseLeafButtonClicked();
          setIsLooseLeafPrintOpen(!isLooseLeafPrintOpen);
        }}
        toggleLooseLeaf2307PrintOpen={() => {
          if (
            (looseLeafPageData?.[0]?.page?.items?.length || 0) >
            linesLimitPerPageOnLooseLeaf
          ) {
            alert(
              `Loose Leaf Receipt can only contain up to ${linesLimitPerPageOnLooseLeaf} lines of items. Please DIVIDE your transaction items to fit the said limit.`
            );
            return;
          }
          onLooseLeafButtonClicked(true);
          setIsLooseLeaf2307PrintOpen(!isLooseLeaf2307PrintOpen);
        }}
        toggleLooseLeafVATExemptedPrintOpen={() => {
          if (
            (looseLeafPageData?.[0]?.page?.items?.length || 0) >
            linesLimitPerPageOnLooseLeaf
          ) {
            alert(
              `Loose Leaf Receipt can only contain up to ${linesLimitPerPageOnLooseLeaf} lines of items. Please DIVIDE your transaction items to fit the said limit.`
            );
            return;
          }
          onLooseLeafButtonClicked();
          setIsLooseLeafVATExemptedPrintOpen(
            !isLooseLeafVATExemptedPrintOpen
          );
        }}
        togglePrintEUOpen={() => setIsPrintEUOpen(!isPrintEUOpen)}
      />

      {(looseLeafPageData?.[0]?.page?.items?.length || 0) >
        linesLimitPerPageOnLooseLeaf && (
          <Alert severity="error" style={{ marginTop: '1rem' }}>
            Loose Leaf Receipt can only contain up to{' '}
            {linesLimitPerPageOnLooseLeaf} lines of items. Please DIVIDE your
            transaction items to fit the said limit.
          </Alert>
        )}

      <Card style={{ margin: '1rem' }}>
        <CardHeader
          title={<Typography variant="h6">Looseleaf Click History</Typography>}
          subheader="History of when looseleaf was tried to be viewed / printed"
        />
        <Divider />

        <CardContent>
          <List aria-label="secondary mailbox folders">
            {looseleafClickHistory?.map((clickLog, index) => (
              <ListItem
                key={index}
                onClick={() => onLooseLeafClickHistoryItemClicked(clickLog)}
                button
                style={{ paddingTop: 0, paddingBottom: 0 }}
              >
                <ListItemText
                  primary={`By: ${clickLog?.internal_created_by}`}
                  secondary={formatDate(clickLog.created_at, 'PPpp')}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Card style={{ margin: '1rem' }}>
        <CardHeader
          title={<Typography variant="h6">Claim Stub Click History</Typography>}
          subheader="History of when claim stub was tried to be viewed / printed"
        />
        <Divider />

        <CardContent>
          <List aria-label="secondary mailbox folders">
            {claimStubClickHistory?.map((clickLog, index) => (
              <ListItem
                key={index}
                onClick={() => setClaimStubHistoryPreview(clickLog)}
                button
                style={{ paddingTop: 0, paddingBottom: 0 }}
              >
                <ListItemText
                  primary={`By: ${clickLog?.internal_created_by}`}
                  secondary={formatDate(clickLog?.created_at, 'PPpp')}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {hasRMA !== 0 ? (
        <RenderRMAEditor
          onUpdateOfRmaItems={onUpdateOfRmaItems}
          items={transactionStateData?.items || []}
        />
      ) : null}
      {isPrintOpen ? (
        <PrintTransactionModal
          overrideOnURL={orderNoOverrideOnURL}
          storeBranch={storeBranch}
          isBlankPage={isBlankPage}
          releasedBy={releasedBy}
          totalAmount={totalPriceOnPrint}
          transactionStateData={transactionStateData}
          documentTitle={documentTitle}
          open={isPrintOpen}
          toggleOpen={() => setIsPrintOpen(!isPrintOpen)}
          pagesData={pagesData}
          dateSold={dateSold}
        />
      ) : null}
      {isPrintEUOpen ? (
        <PrintTransactionModalEU
          overrideOnURL={orderNoOverrideOnURL}
          storeBranch={storeBranch}
          isBlankPage={isBlankPage}
          releasedBy={releasedBy}
          totalAmount={totalPriceOnPrint}
          transactionStateData={transactionStateData}
          documentTitle={documentTitle}
          open={isPrintEUOpen}
          toggleOpen={() => setIsPrintEUOpen(!isPrintEUOpen)}
          pagesData={pagesData}
          dateSold={dateSold}
        />
      ) : null}
      {isLooseLeafPrintOpen ? (
        <PrintTransactionModalLooseLeaf
          dateSold={dateSold}
          pagesData={looseLeafPageData}
          releasedBy={releasedBy}
          storeBranch={storeBranch}
          isBlankPage={isBlankPage}
          open={isLooseLeafPrintOpen}
          documentTitle={documentTitle}
          totalAmount={totalPriceOnPrint}
          overrideOnURL={orderNoOverrideOnURL}
          transactionStateData={transactionStateData}
          looseLeafBusinessInfo={looseLeafBusinessInfo}
          toggleOpen={() => setIsLooseLeafPrintOpen(!isLooseLeafPrintOpen)}
        />
      ) : null}
      {isLooseLeaf2307PrintOpen ? (
        <PrintTransactionModalLooseLeaf
          isTwoThreeOSeven
          dateSold={dateSold}
          pagesData={looseLeafPageData}
          releasedBy={releasedBy}
          storeBranch={storeBranch}
          isBlankPage={isBlankPage}
          open={isLooseLeaf2307PrintOpen}
          documentTitle={documentTitle}
          totalAmount={totalPriceOnPrint}
          overrideOnURL={orderNoOverrideOnURL}
          looseLeafBusinessInfo={looseLeafBusinessInfo}
          transactionStateData={transactionStateData}
          toggleOpen={() =>
            setIsLooseLeaf2307PrintOpen(!isLooseLeaf2307PrintOpen)
          }
        />
      ) : null}
      {isLooseLeafVATExemptedPrintOpen ? (
        <PrintTransactionModalLooseLeaf
          isVATExempted
          dateSold={dateSold}
          pagesData={looseLeafPageData}
          releasedBy={releasedBy}
          storeBranch={storeBranch}
          isBlankPage={isBlankPage}
          open={isLooseLeafVATExemptedPrintOpen}
          documentTitle={documentTitle}
          totalAmount={totalPriceOnPrint}
          overrideOnURL={orderNoOverrideOnURL}
          looseLeafBusinessInfo={looseLeafBusinessInfo}
          transactionStateData={transactionStateData}
          toggleOpen={() =>
            setIsLooseLeafVATExemptedPrintOpen(
              !isLooseLeafVATExemptedPrintOpen
            )
          }
        />
      ) : null}

      {looseLeafHistoryPreviewData?.looseleaf_page_data ? (
        <PrintTransactionModalLooseLeaf
          dateSold={looseLeafHistoryPreviewData.date_sold}
          pagesData={looseLeafHistoryPreviewData?.looseleaf_page_data}
          releasedBy={looseLeafHistoryPreviewData?.released_by || ''}
          storeBranch={looseLeafHistoryPreviewData?.store_branch || ''}
          isBlankPage={false}
          documentTitle={looseLeafHistoryPreviewData?.document_title || ''}
          totalAmount={looseLeafHistoryPreviewData?.total_amount || 0}
          transactionStateData={
            looseLeafHistoryPreviewData?.transaction_state_data
          }
          looseLeafBusinessInfo={
            looseLeafHistoryPreviewData?.looseleaf_business_info
          }
          open={looseLeafHistoryPreviewData ? true : false}
          toggleOpen={() => setLooseLeafHistoryPreviewData(undefined)}
        />
      ) : null}

      {claimStubHistoryPreview ? (
        <ClaimStubPreviewModal
          open={claimStubHistoryPreview ? true : false}
          toggleOpen={() => setClaimStubHistoryPreview(undefined)}
          claimStubHistoryData={
            claimStubHistoryPreview?.claim_stub_data_readable
          }
        />
      ) : null}
    </div>
  );
};

export default MyDocument;
